import React, { useEffect, useState } from "react";
import { request, Settings, displayInstant } from "./api";
interface Change {
  id: string;
  kind: "policy" | "access";
  version: number;
  effectiveFrom: string;
  recordedAt: string;
  actorUserId: string;
  reason: string;
  settings: Settings | null;
  receptionEnabled: boolean | null;
}
export function SettingsHistory({ timezone }: { timezone: string }) {
  const [items, setItems] = useState<Change[]>([]),
    [cursor, setCursor] = useState<string | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  async function load(next?: string) {
    setBusy(true);
    setError("");
    try {
      const result = await request<{
        items: Change[];
        nextCursor: string | null;
      }>(
        `/settings/history?limit=50${next ? `&cursor=${encodeURIComponent(next)}` : ""}`,
      );
      setItems((old) => (next ? [...old, ...result.items] : result.items));
      setCursor(result.nextCursor);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    void load();
    const refresh = () => load();
    window.addEventListener("housekeeping-settings-changed", refresh);
    return () =>
      window.removeEventListener("housekeeping-settings-changed", refresh);
  }, []);
  return (
    <section className="pfres-section">
      <div className="pfres-section__header">
        <h2 className="pfres-section__title">Settings audit history</h2>
        <p className="pfres-section__lede">
          Cleaning-rule versions and reception access changes. Room cleaning
          actions are in the History tab.
        </p>
      </div>
      {error && (
        <p className="text-danger" role="alert">
          {error}
        </p>
      )}
      {items.map((change) => (
        <details
          className="pfres-panel pfres-panel--quiet mb-2"
          key={change.id}
        >
          <summary>
            {change.kind === "access" ? "Reception access" : "Cleaning rules"} ·
            version {change.version} ·{" "}
            {displayInstant(change.recordedAt, timezone)}
          </summary>
          <p>
            By {change.actorUserId} · Effective{" "}
            {displayInstant(change.effectiveFrom, timezone)}
            <br />
            Reason: {change.reason}
          </p>
          {change.kind === "access" ? (
            <p>Reception {change.receptionEnabled ? "enabled" : "disabled"}</p>
          ) : (
            change.settings && (
              <>
                <p>
                  Timezone {change.settings.timezone} · Day boundary{" "}
                  {change.settings.serviceDayBoundary}
                </p>
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Code / label</th>
                        <th>Description</th>
                        <th>Trigger / nights</th>
                        <th>Minutes / rank</th>
                        <th>Covers</th>
                        <th>Retired</th>
                      </tr>
                    </thead>
                    <tbody>
                      {change.settings.rules.map((rule) => (
                        <tr key={rule.id}>
                          <td>
                            {rule.code} / {rule.label}
                            <span
                              className="d-block"
                              style={{
                                borderBottom: `4px solid ${rule.color}`,
                              }}
                            />
                          </td>
                          <td>{rule.description}</td>
                          <td>
                            {rule.trigger} / {rule.intervalNights ?? ""}
                          </td>
                          <td>
                            {rule.minutes} / {rule.rank}
                          </td>
                          <td>
                            {rule.coverageRuleIds
                              .map(
                                (id) =>
                                  change.settings!.rules.find(
                                    (r) => r.id === id,
                                  )?.code || id,
                              )
                              .join(", ")}
                          </td>
                          <td>{rule.retired ? "Yes" : "No"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )
          )}
        </details>
      ))}
      {busy && <p role="status">Loading audit history...</p>}
      {cursor && (
        <button
          type="button"
          className="btn btn-outline-contrast"
          disabled={busy}
          onClick={() => load(cursor)}
        >
          Load older changes
        </button>
      )}
    </section>
  );
}
